import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Printer, ArrowLeft, MapPin, Copy, Check } from "lucide-react";
import api, { fileUrl } from "../lib/api";
import { TC_LOGO_URL } from "../lib/constants";
import { toast } from "sonner";

/**
 * Print-friendly facilitator bio page.
 * Designed for both screen viewing and Print -> Save as PDF -> Attach to email.
 */
export default function FacilitatorBio() {
  const { id } = useParams();
  const [f, setF] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  // Inline the headshot as a base64 data URL so the print preview always renders
  // it (browsers commonly strip CSS background-image when printing, and some
  // print engines won't follow auth-tokenized URLs).
  const [photoDataUri, setPhotoDataUri] = useState("");

  useEffect(() => {
    api.get(`/facilitators/${id}`)
      .then(({ data }) => setF(data))
      .catch(() => setF(null))
      .finally(() => setLoading(false));
  }, [id]);

  // Fetch the headshot bytes and convert to a base64 data URI for print-safe rendering.
  useEffect(() => {
    if (!f?.photo_path) return;
    let cancelled = false;
    fetch(fileUrl(f.photo_path))
      .then((r) => (r.ok ? r.blob() : null))
      .then((blob) => {
        if (!blob || cancelled) return;
        const reader = new FileReader();
        reader.onloadend = () => !cancelled && setPhotoDataUri(reader.result || "");
        reader.readAsDataURL(blob);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [f?.photo_path]);

  const copyToClipboard = async () => {
    if (!f) return;
    // Client-safe export: no email or phone. Only public-facing bio info.
    const lines = [
      f.name,
      [f.city, f.state, f.country].filter(Boolean).join(", "),
      "",
      f.bio || "",
    ];
    if (f.specialties?.length) {
      lines.push("");
      lines.push("Specialties: " + f.specialties.join(", "));
    }
    if (f.languages?.length) lines.push("Languages: " + f.languages.join(", "));
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      setCopied(true);
      toast.success("Bio copied. Paste into your email.");
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error("Couldn't copy. Try selecting and copying manually.");
    }
  };

  if (loading) return <div className="p-10 text-center text-slate-400">Loading…</div>;
  if (!f) return <div className="p-10 text-center text-slate-500">Bio not found.</div>;

  return (
    <div className="bg-slate-50 min-h-screen print:bg-white">
      <style>{`
        @media print {
          @page { margin: 0.4in; }
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        }
      `}</style>
      {/* Action bar — hidden in print */}
      <div className="print:hidden bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link
            to={`/facilitators?id=${id}`}
            data-testid="bio-back"
            className="inline-flex items-center gap-1 text-sm font-bold text-slate-500 hover:text-tc-ink"
          >
            <ArrowLeft size={14} /> Back to Facilitators
          </Link>
          <div className="flex items-center gap-2">
            <button
              onClick={copyToClipboard}
              data-testid="bio-copy"
              className="h-9 px-3.5 rounded-lg border-2 border-slate-200 hover:border-[#0091be] hover:text-[#0091be] text-sm font-bold inline-flex items-center gap-1.5 transition-colors"
            >
              {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy bio</>}
            </button>
            <button
              onClick={() => window.print()}
              data-testid="bio-print"
              className="h-9 px-3.5 rounded-lg bg-[#7ac143] hover:bg-[#5a9c2f] text-white text-sm font-bold inline-flex items-center gap-1.5 transition-colors"
            >
              <Printer size={14} /> Print / Save PDF
            </button>
          </div>
        </div>
      </div>

      {/* Printable card */}
      <article
        className="max-w-3xl mx-auto bg-white p-8 sm:p-12 my-6 print:my-0 print:p-8 rounded-xl print:rounded-none shadow-sm print:shadow-none"
        data-testid="bio-printable"
      >
        {/* Header */}
        <header className="flex items-center justify-between pb-6 border-b border-slate-200">
          <img src={TC_LOGO_URL} alt="True Colors International" className="h-10 w-auto object-contain" />
          <div className="text-right text-xs uppercase tracking-[0.18em] font-bold text-slate-400">
            Facilitator Bio
          </div>
        </header>

        {/* Photo + Identity */}
        <div className="mt-8 flex items-start gap-6 flex-wrap sm:flex-nowrap">
          {f.photo_path && photoDataUri ? (
            <img
              src={photoDataUri}
              alt={f.name}
              className="w-32 h-32 rounded-full object-cover shrink-0 ring-4 ring-white shadow-md border-2"
              style={{ borderColor: "#7ac143", printColorAdjust: "exact", WebkitPrintColorAdjust: "exact" }}
            />
          ) : (
            <div
              className="w-32 h-32 rounded-full flex items-center justify-center font-display text-4xl shrink-0"
              style={{ backgroundColor: "rgba(122,193,67,0.15)", color: "#7ac143", printColorAdjust: "exact", WebkitPrintColorAdjust: "exact" }}
            >
              {f.name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-3xl sm:text-4xl text-tc-ink leading-tight">{f.name}</h1>
            {(f.city || f.state || f.country) && (
              <div className="mt-2 inline-flex items-center gap-1.5 text-sm text-slate-600">
                <MapPin size={14} className="text-[#7ac143]" /> {[f.city, f.state, f.country].filter(Boolean).join(", ")}
              </div>
            )}
          </div>
        </div>

        {/* Bio body */}
        {f.bio && (
          <section className="mt-8">
            <p className="text-[15px] leading-7 text-slate-700 whitespace-pre-line">{f.bio}</p>
          </section>
        )}

        {/* Specialties + Languages */}
        {(f.specialties?.length > 0 || f.languages?.length > 0) && (
          <section className="mt-8 pt-6 border-t border-slate-200 grid sm:grid-cols-2 gap-6 text-sm">
            {f.specialties?.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-[0.18em] font-bold text-[#7ac143] mb-2">Specialties</div>
                <div className="flex flex-wrap gap-1.5">
                  {f.specialties.map((s) => (
                    <span
                      key={s}
                      className="inline-block px-2.5 py-1 rounded-full text-xs font-medium"
                      style={{ backgroundColor: "rgba(122,193,67,0.12)", color: "#3f6e1c" }}
                    >
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {f.languages?.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-[0.18em] font-bold text-[#0091be] mb-2">Languages</div>
                <div className="flex flex-wrap gap-1.5">
                  {f.languages.map((l) => (
                    <span
                      key={l}
                      className="inline-block px-2.5 py-1 rounded-full text-xs font-medium"
                      style={{ backgroundColor: "rgba(0,145,190,0.12)", color: "#0091be" }}
                    >
                      {l}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        <footer className="mt-12 pt-6 border-t border-slate-200 text-xs text-slate-400 text-center">
          True Colors International · truecolorsintl.com
        </footer>
      </article>
    </div>
  );
}
